// src/pages/contracts/MandatoryContractPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  FileText,
  AlertTriangle,
  CheckCircle,
  Pen,
  Download,
  LogOut,
  ChevronRight,
  ChevronLeft,
  Eye,
  Shield,
  Type,
  PenTool
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { contractService } from '../../services/contract.service';
import { Contract, SigningAuditEvent } from '../../types/contract.types';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { SignatureCanvas } from '../../components/contracts/SignatureCanvas';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import {
  getClientIP,
  getUserAgent,
  hashDocument,
  createAuditEvent,
  generateSigningCertificate,
  getGeolocation,
  generateMergedComplianceDocument
} from '../../utils/signingUtils';

type SigningStep = 'review' | 'acknowledge' | 'consent' | 'sign';
type SignatureMode = 'draw' | 'type';

const MandatoryContractPage: React.FC = () => {
  const { currentUser, userProfile, logout } = useAuth();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);

  // Multi-step e-sign flow
  const [currentStep, setCurrentStep] = useState<SigningStep>('review');
  const [hasReviewed, setHasReviewed] = useState(false);
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [hasConsented, setHasConsented] = useState(false);
  const [signatureMode, setSignatureMode] = useState<SignatureMode>('draw');
  const [typedSignature, setTypedSignature] = useState('');
  const [signerTitle, setSignerTitle] = useState('');

  // Legal defensibility data
  const [clientIP, setClientIP] = useState<string>('');
  const [userAgent, setUserAgent] = useState<string>('');
  const [documentHash, setDocumentHash] = useState<string>('');
  const [auditTrail, setAuditTrail] = useState<SigningAuditEvent[]>([]);
  const [geolocation, setGeolocation] = useState<{ latitude: number; longitude: number; accuracy: number } | null>(null);

  // Download dropdown
  const [downloadDropdownOpen, setDownloadDropdownOpen] = useState(false);
  const [generatingPackage, setGeneratingPackage] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Handle downloading the complete compliance package (original + e-sign certificate merged)
  const handleDownloadPackage = async () => {
    if (!selectedContract) return;
    try {
      setGeneratingPackage(true);
      setDownloadDropdownOpen(false);

      const blob = await generateMergedComplianceDocument(selectedContract as any);

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedContract.title.replace(/[^a-zA-Z0-9]/g, '_')}_Compliance_Package.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Compliance package downloaded');
    } catch (error) {
      console.error('Error generating compliance package:', error);
      toast.error('Failed to generate compliance package');
    } finally {
      setGeneratingPackage(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      loadMandatoryContracts();
    }
  }, [currentUser]);

  // Initialize legal data when component mounts
  useEffect(() => {
    const initializeLegalData = async () => {
      // Get IP address
      const ip = await getClientIP();
      setClientIP(ip);

      // Get user agent
      const ua = getUserAgent();
      setUserAgent(ua);

      // Try to get geolocation (optional - user may deny)
      const geo = await getGeolocation();
      setGeolocation(geo);
    };

    initializeLegalData();
  }, []);

  // Hash document and create audit event when contract is selected
  useEffect(() => {
    const initializeContractSession = async () => {
      if (!selectedContract || !currentUser || !userProfile || !clientIP) return;

      try {
        // Hash the document
        const hash = await hashDocument(selectedContract.documentUrl);
        setDocumentHash(hash);

        // Store hash on contract if not already set
        if (!selectedContract.documentHash) {
          await contractService.setDocumentHash(selectedContract.id, hash);
        }

        // Create document opened audit event
        const openedEvent = createAuditEvent(
          'document_opened',
          currentUser.uid,
          userProfile.name,
          userProfile.email,
          clientIP,
          userAgent,
          { contractId: selectedContract.id, documentHash: hash }
        );
        setAuditTrail([openedEvent]);
      } catch (error) {
        console.error('Error initializing contract session:', error);
      }
    };

    initializeContractSession();
  }, [selectedContract, currentUser, userProfile, clientIP, userAgent]);

  const loadMandatoryContracts = async () => {
    if (!currentUser) return;

    try {
      const mandatoryContracts = await contractService.getMandatoryUnsignedContracts(currentUser.uid);
      setContracts(mandatoryContracts);
      if (mandatoryContracts.length > 0 && !selectedContract) {
        setSelectedContract(mandatoryContracts[0]);
      }
    } catch (error) {
      console.error('Error loading mandatory contracts:', error);
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const resetFlow = () => {
    setCurrentStep('review');
    setHasReviewed(false);
    setHasAcknowledged(false);
    setHasConsented(false);
    setSignatureData(null);
    setTypedSignature('');
    setAuditTrail([]);
    setDocumentHash('');
  };

  // Add an audit event to the trail
  const addAuditEvent = (eventType: SigningAuditEvent['eventType'], metadata?: Record<string, any>) => {
    if (!currentUser || !userProfile || !clientIP) return;

    const event = createAuditEvent(
      eventType,
      currentUser.uid,
      userProfile.name,
      userProfile.email,
      clientIP,
      userAgent,
      metadata
    );
    setAuditTrail(prev => [...prev, event]);
  };

  const handleContractSelect = (contract: Contract) => {
    setSelectedContract(contract);
    resetFlow();
  };

  const handleSign = async () => {
    if (!currentUser || !userProfile || !selectedContract) return;

    const finalSignature = signatureMode === 'type'
      ? generateTypedSignatureImage(typedSignature)
      : signatureData;

    if (!finalSignature) return;

    setSigning(true);
    try {
      const signedAt = new Date();

      // Add signature applied audit event
      addAuditEvent('signature_applied', {
        signatureType: signatureMode === 'type' ? 'typed' : 'drawn'
      });

      // Build the complete audit trail including the signing completed event
      const signingCompletedEvent = createAuditEvent(
        'signing_completed',
        currentUser.uid,
        userProfile.name,
        userProfile.email,
        clientIP,
        userAgent,
        { documentHash }
      );
      const fullAuditTrail = [...auditTrail, signingCompletedEvent];

      // Generate signing certificate
      const certificateBlob = await generateSigningCertificate(
        selectedContract.title,
        selectedContract.contractType || 'general',
        documentHash || 'hash-unavailable',
        userProfile.name,
        userProfile.email,
        signatureMode === 'type' ? 'typed' : 'drawn',
        signedAt,
        clientIP,
        userAgent,
        fullAuditTrail,
        finalSignature
      );

      // Add certificate generated event
      const certificateEvent = createAuditEvent(
        'certificate_generated',
        currentUser.uid,
        userProfile.name,
        userProfile.email,
        clientIP,
        userAgent
      );
      const finalAuditTrail = [...fullAuditTrail, certificateEvent];

      // Add signature with all legal defensibility data
      // Build signature object, only including geolocation if it exists
      const signaturePayload: any = {
        fieldId: 'main',
        signedBy: currentUser.uid,
        signedByName: userProfile.name || 'Unknown',
        signedByEmail: userProfile.email || 'Unknown',
        signatureData: finalSignature,
        signatureType: signatureMode === 'type' ? 'typed' : 'drawn',
        ipAddress: clientIP || 'unknown',
        userAgent: userAgent || 'unknown',
        documentHash: documentHash || 'hash-unavailable',
        auditTrail: finalAuditTrail || []
      };

      // Only add geolocation if it exists (don't pass undefined)
      if (geolocation) {
        signaturePayload.geolocation = geolocation;
      }

      // Add signer title if provided
      if (signerTitle.trim()) {
        signaturePayload.signerTitle = signerTitle.trim();
      }

      await contractService.addSignature(
        selectedContract.id,
        signaturePayload,
        certificateBlob
      );

      // Update contract with signer title if provided
      if (signerTitle.trim()) {
        await updateDoc(doc(db, 'contracts', selectedContract.id), {
          signerTitle: signerTitle.trim(),
          updatedAt: Timestamp.now()
        });
      }

      // Save signature profile
      await contractService.saveSignatureProfile(
        currentUser.uid,
        finalSignature,
        signatureMode === 'type' ? 'typed' : 'drawn'
      );

      // Complete the contract signature task
      await contractService.completeContractTask(selectedContract.id, currentUser.uid);

      toast.success('Contract signed successfully!');

      // Reload contracts
      resetFlow();
      await loadMandatoryContracts();

      // If no more contracts, page will refresh and user will be unblocked
      if (contracts.length <= 1) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error signing contract:', error);
      toast.error('Failed to sign contract');
    } finally {
      setSigning(false);
    }
  };

  // Generate an image from typed text for signature
  const generateTypedSignatureImage = (text: string): string => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 100;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#1e293b';
    ctx.font = 'italic 36px "Brush Script MT", cursive, serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);

    return canvas.toDataURL('image/png');
  };

  const getContractTypeLabel = (type?: string) => {
    switch (type) {
      case 'w9': return 'W-9 Tax Form';
      case 'direct_deposit': return 'Direct Deposit Authorization';
      case 'employment': return 'Employment Agreement';
      case 'nda': return 'Non-Disclosure Agreement';
      case 'service_agreement': return 'Service Agreement';
      case 'amendment': return 'Contract Amendment';
      default: return 'Contract';
    }
  };

  const getStepNumber = (step: SigningStep): number => {
    const steps: SigningStep[] = ['review', 'acknowledge', 'consent', 'sign'];
    return steps.indexOf(step) + 1;
  };

  const isServiceAgreement = selectedContract?.contractType === 'service_agreement' || selectedContract?.contractType === 'amendment';

  const canProceedToNext = (): boolean => {
    switch (currentStep) {
      case 'review': return hasReviewed;
      case 'acknowledge': return hasAcknowledged;
      case 'consent': return hasConsented;
      case 'sign': {
        const hasSignature = signatureMode === 'type' ? typedSignature.length >= 2 : !!signatureData;
        if (isServiceAgreement) return hasSignature && signerTitle.trim().length > 0;
        return hasSignature;
      }
      default: return false;
    }
  };

  const goToNextStep = () => {
    const steps: SigningStep[] = ['review', 'acknowledge', 'consent', 'sign'];
    const currentIndex = steps.indexOf(currentStep);

    // Add audit event for the step being completed
    if (currentStep === 'review') {
      addAuditEvent('document_reviewed');
    } else if (currentStep === 'acknowledge') {
      addAuditEvent('acknowledgment_accepted');
    } else if (currentStep === 'consent') {
      addAuditEvent('esign_consent_given');
    }

    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
    }
  };

  const goToPreviousStep = () => {
    const steps: SigningStep[] = ['review', 'acknowledge', 'consent', 'sign'];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-amber-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Contract Required</h1>
              <p className="text-sm text-gray-500">Please review and sign to continue</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Alert Banner */}
        <div className="bg-amber-100 border border-amber-300 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <h2 className="font-medium text-amber-800">Action Required</h2>
              <p className="text-sm text-amber-700 mt-1">
                You have {contracts.length} mandatory contract{contracts.length !== 1 ? 's' : ''} that must be signed before you can access the application.
              </p>
            </div>
          </div>
        </div>

        {/* Contract List */}
        {contracts.length > 1 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
            <h3 className="font-medium text-gray-800 mb-3">Contracts to Sign ({contracts.length})</h3>
            <div className="space-y-2">
              {contracts.map((contract, index) => (
                <button
                  key={contract.id}
                  onClick={() => handleContractSelect(contract)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    selectedContract?.id === contract.id
                      ? 'bg-primary-50 border-2 border-primary-500'
                      : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${
                    selectedContract?.id === contract.id ? 'bg-primary-500' : 'bg-gray-400'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-800">{contract.title}</div>
                    <div className="text-xs text-gray-500">{getContractTypeLabel(contract.contractType)}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Selected Contract - E-Sign Flow */}
        {selectedContract && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            {/* Progress Steps */}
            <div className="bg-gray-50 border-b border-gray-200 p-4">
              <div className="flex items-center justify-between max-w-md mx-auto">
                {(['review', 'acknowledge', 'consent', 'sign'] as SigningStep[]).map((step, index) => (
                  <React.Fragment key={step}>
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-colors ${
                        getStepNumber(currentStep) > index + 1
                          ? 'bg-accent-500 text-white'
                          : getStepNumber(currentStep) === index + 1
                            ? 'bg-primary-500 text-white'
                            : 'bg-gray-200 text-gray-500'
                      }`}>
                        {getStepNumber(currentStep) > index + 1 ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          index + 1
                        )}
                      </div>
                      <span className={`text-xs mt-1 ${
                        getStepNumber(currentStep) >= index + 1 ? 'text-gray-700' : 'text-gray-400'
                      }`}>
                        {step === 'review' && 'Review'}
                        {step === 'acknowledge' && 'Acknowledge'}
                        {step === 'consent' && 'Consent'}
                        {step === 'sign' && 'Sign'}
                      </span>
                    </div>
                    {index < 3 && (
                      <div className={`flex-1 h-1 mx-2 rounded ${
                        getStepNumber(currentStep) > index + 1 ? 'bg-accent-500' : 'bg-gray-200'
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Contract Header */}
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-primary-50 to-white">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-primary-100 rounded-xl">
                  <FileText className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-800">{selectedContract.title}</h2>
                  <p className="text-sm text-gray-500">{getContractTypeLabel(selectedContract.contractType)}</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {/* Step 1: Review Document */}
              {currentStep === 'review' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-gray-700 mb-4">
                    <Eye className="h-5 w-5 text-primary-500" />
                    <h3 className="font-medium">Step 1: Review Document</h3>
                  </div>

                  <p className="text-sm text-gray-600 mb-4">
                    Please review the entire document below. You must confirm you have read it before proceeding.
                  </p>

                  {/* PDF Viewer */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-100">
                    <iframe
                      ref={iframeRef}
                      src={`${selectedContract.documentUrl}#toolbar=1&navpanes=0`}
                      className="w-full h-[500px]"
                      title="Contract Document"
                    />
                  </div>

                  <div className="flex items-center justify-between pt-4">
                    {/* Download Compliance Package */}
                    <button
                      onClick={handleDownloadPackage}
                      disabled={generatingPackage || selectedContract.signatures.length === 0}
                      className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                      title={selectedContract.signatures.length === 0 ? 'No signatures yet' : 'Download compliance package (original + e-sign certificate)'}
                    >
                      {generatingPackage ? (
                        <>
                          <div className="h-4 w-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4" />
                          Download
                        </>
                      )}
                    </button>

                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={hasReviewed}
                        onChange={(e) => setHasReviewed(e.target.checked)}
                        className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        I have reviewed this document
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {/* Step 2: Acknowledge */}
              {currentStep === 'acknowledge' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-gray-700 mb-4">
                    <CheckCircle className="h-5 w-5 text-primary-500" />
                    <h3 className="font-medium">Step 2: Acknowledge Understanding</h3>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                    <p className="text-gray-700">
                      By checking the box below, you acknowledge that:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-gray-600 text-sm">
                      <li>You have read and understand the terms of the document "{selectedContract.title}"</li>
                      <li>You have had the opportunity to ask questions about the document</li>
                      <li>You are entering into this agreement voluntarily</li>
                      <li>You understand the obligations and responsibilities outlined in this document</li>
                    </ul>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer p-4 bg-primary-50 rounded-xl border-2 border-primary-200">
                    <input
                      type="checkbox"
                      checked={hasAcknowledged}
                      onChange={(e) => setHasAcknowledged(e.target.checked)}
                      className="w-5 h-5 mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">
                      <strong>I acknowledge</strong> that I have read, understand, and agree to be bound by the terms and conditions of this document.
                    </span>
                  </label>
                </div>
              )}

              {/* Step 3: E-Sign Consent */}
              {currentStep === 'consent' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-gray-700 mb-4">
                    <Shield className="h-5 w-5 text-primary-500" />
                    <h3 className="font-medium">Step 3: Electronic Signature Consent</h3>
                  </div>

                  <div className="bg-blue-50 rounded-xl p-6 space-y-4 border border-blue-200">
                    <h4 className="font-medium text-blue-800">Electronic Signature Disclosure</h4>
                    <div className="text-sm text-blue-700 space-y-3">
                      <p>
                        By proceeding with an electronic signature, you consent to the following:
                      </p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Your electronic signature is legally binding and has the same force and effect as a handwritten signature</li>
                        <li>You consent to conduct this transaction electronically</li>
                        <li>You can receive a copy of the signed document and a certificate of signature for your records</li>
                        <li>Your signature will be recorded along with the date, time, IP address, and browser information</li>
                        <li>A SHA-256 hash of the document will be recorded to verify document integrity</li>
                      </ul>
                      <p className="pt-2">
                        This consent is given in accordance with the Electronic Signatures in Global and National Commerce Act (E-SIGN Act) and the Uniform Electronic Transactions Act (UETA).
                      </p>
                    </div>
                  </div>

                  {/* Technical details being captured */}
                  <div className="bg-gray-50 rounded-xl p-4 text-xs text-gray-600 space-y-1">
                    <p className="font-medium text-gray-700">Information being recorded:</p>
                    <p><strong>IP Address:</strong> {clientIP || 'Detecting...'}</p>
                    <p><strong>Document Hash:</strong> {documentHash ? `${documentHash.substring(0, 32)}...` : 'Calculating...'}</p>
                    <p><strong>Timestamp:</strong> {format(new Date(), 'MMMM d, yyyy \'at\' h:mm:ss a')}</p>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer p-4 bg-accent-50 rounded-xl border-2 border-accent-200">
                    <input
                      type="checkbox"
                      checked={hasConsented}
                      onChange={(e) => setHasConsented(e.target.checked)}
                      className="w-5 h-5 mt-0.5 rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                    />
                    <span className="text-sm text-gray-700">
                      <strong>I consent</strong> to use an electronic signature and understand that my electronic signature is legally binding.
                    </span>
                  </label>
                </div>
              )}

              {/* Step 4: Sign */}
              {currentStep === 'sign' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-gray-700 mb-4">
                    <Pen className="h-5 w-5 text-primary-500" />
                    <h3 className="font-medium">Step 4: Sign Document</h3>
                  </div>

                  {/* Signature Mode Toggle */}
                  <div className="flex gap-2 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setSignatureMode('draw');
                        setTypedSignature('');
                      }}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                        signatureMode === 'draw'
                          ? 'bg-primary-100 text-primary-700 border-2 border-primary-300'
                          : 'bg-gray-100 text-gray-600 border-2 border-transparent'
                      }`}
                    >
                      <PenTool className="h-4 w-4" />
                      Draw Signature
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSignatureMode('type');
                        setSignatureData(null);
                      }}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                        signatureMode === 'type'
                          ? 'bg-primary-100 text-primary-700 border-2 border-primary-300'
                          : 'bg-gray-100 text-gray-600 border-2 border-transparent'
                      }`}
                    >
                      <Type className="h-4 w-4" />
                      Type Signature
                    </button>
                  </div>

                  {signatureMode === 'draw' ? (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600">
                        Draw your signature in the box below using your mouse or finger.
                      </p>
                      <SignatureCanvas
                        onSave={setSignatureData}
                        onClear={() => setSignatureData(null)}
                      />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600">
                        Type your full legal name below. This will be converted to a signature style.
                      </p>
                      <input
                        type="text"
                        value={typedSignature}
                        onChange={(e) => setTypedSignature(e.target.value)}
                        placeholder="Type your full name"
                        className="w-full px-4 py-3 text-xl border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      {typedSignature && (
                        <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 bg-white">
                          <p className="text-xs text-gray-500 mb-2">Signature Preview:</p>
                          <p className="text-3xl italic font-['Brush_Script_MT',cursive,serif] text-gray-800 text-center">
                            {typedSignature}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Signer Title (required for service agreements) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title / Position {isServiceAgreement ? '*' : '(Optional)'}
                    </label>
                    <input
                      type="text"
                      value={signerTitle}
                      onChange={(e) => setSignerTitle(e.target.value)}
                      placeholder="e.g., CEO, Principal, Director"
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    {isServiceAgreement && !signerTitle.trim() && (
                      <p className="text-xs text-amber-600 mt-1">Title is required for service agreements.</p>
                    )}
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600">
                    <p>
                      <strong>Signing as:</strong> {userProfile?.name} ({userProfile?.email})
                    </p>
                    {signerTitle && (
                      <p className="mt-1">
                        <strong>Title:</strong> {signerTitle}
                      </p>
                    )}
                    <p className="mt-1">
                      <strong>Date:</strong> {format(new Date(), 'MMMM d, yyyy \'at\' h:mm a')}
                    </p>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
                {currentStep !== 'review' ? (
                  <button
                    onClick={goToPreviousStep}
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Back
                  </button>
                ) : (
                  <div />
                )}

                {currentStep !== 'sign' ? (
                  <button
                    onClick={goToNextStep}
                    disabled={!canProceedToNext()}
                    className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue
                    <ChevronRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    onClick={handleSign}
                    disabled={!canProceedToNext() || signing}
                    className="flex items-center gap-2 px-6 py-2 bg-accent-600 text-white rounded-xl hover:bg-accent-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {signing ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        Signing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        Complete & Sign
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Having trouble? Contact your administrator for assistance.</p>
        </div>
      </div>
    </div>
  );
};

export default MandatoryContractPage;
